import { useTheme } from '@emotion/react';
import { ReactComponent as CloseIcon } from "assets/close_icon.svg";
import { Field, FormikProvider, useFormik } from 'formik';
import React from 'react';
import { Box, CircularProgress, Grid, IconButton, Paper, Typography, MenuItem, InputAdornment, Chip, Autocomplete, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CustomInputField from 'ui-components/formik/CustomInputField';
import ModalComponent from 'ui-components/ModalComponent';
import { Primary } from 'ui-components/Buttons';
import * as Yup from "yup";
import { useNavigate } from 'react-router-dom';
import { currencyInputProps, formatAllowsNegativeCurrency, plans, transformPlans } from 'utils/index';
import { CreatePromoCode as CreatePromoCodeAPI, UpdatePromoCode as UpdatePromoCodeAPI } from 'api/admin_settings';
import { useToast } from 'context/ToastContext';
import CustomValidationText from 'ui-components/CustomValidationText';
import CustomLabel from 'ui-components/CustomLabel';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { FetchAllStartupPlans } from 'api/utils'

const useStyles = makeStyles({
    customInput: {},
});

const discountTypes = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'flat', label: 'Flat' }
];

const offerTypes = [
    { value: 'discount', label: 'Discount' },
    // Add more if needed
];

/**
 * A plan option as it appears in the selector: the plan and its frequency, with
 * the version as attached metadata rather than part of the name. Plans share a
 * title across versions, so without this "Plus (Yearly)" appears twice.
 */
const PlanOptionLabel = ({ option }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        {`${option?.plan_title || ''} ${option?.frequency ? option.frequency.charAt(0).toUpperCase() + option.frequency.slice(1) : ''}`.trim()}
        {option?.version ? (
            <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, opacity: 0.75 }}>
                {`V${option.version}`}
            </Box>
        ) : null}
    </Box>
);

/** Current versions tinted, superseded ones muted — as in the offer listing. */
const planChipSx = (option) => {
    const isCurrent = !option?.version || option.version > 1;

    return {
        height: '24px',
        fontSize: '12px',
        fontWeight: 500,
        borderRadius: '6px',
        backgroundColor: isCurrent ? '#EEF2FF' : '#F3F4F6',
        color: isCurrent ? '#4338CA' : '#6B7280',
        border: `1px solid ${isCurrent ? '#C7D2FE' : '#E5E7EB'}`,
        '& .MuiChip-deleteIcon': { color: isCurrent ? '#818CF8' : '#9CA3AF' },
    };
};

const UpdatePromoCode = ({ open = false, handleClose, selectedItem, onSuccess }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { addToast } = useToast();

    // console.log(selectedItem,"selectedItem")

    const initialValues = {
        // planPrice: selectedItem?.amount || '',
        offer_id: selectedItem?.offer_id || '',
        plan_id: selectedItem?.plan_id || '',
        offer_code: selectedItem?.offer_code || '',
        discount_type: selectedItem?.discount_type || '',
        status: selectedItem?.status || 'active',
        // discount_value: selectedItem?.discount_value || '',
        // offer_type: selectedItem?.offer_type || '',
        valid_from: selectedItem?.valid_from ? selectedItem.valid_from.slice(0, 10) : '',
        valid_to: selectedItem?.valid_to ? selectedItem.valid_to.slice(0, 10) : '',
    };

    const { mutateAsync: createPromoCodeAsync, isLoading: isCreatingPromoCode } = CreatePromoCodeAPI();
    const { mutateAsync: updatePromoCodeAsync, isLoading: isUpdatingPromoCode } = UpdatePromoCodeAPI();
    const { data: startupPlans, isLoading: isStartupPlansLoading } = FetchAllStartupPlans();

    const planOptions = React.useMemo(() => transformPlans(startupPlans), [startupPlans]);

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: Yup.object({
            // planPrice: Yup.string().required("Plan Price is required"),
            offer_id: Yup.string().required("Offer ID is required"),
            plan_id: Yup.array().min(1, "Plan is required"),
            offer_code: Yup.string().required("Offer Code is required"),
            discount_type: Yup.string().required("Discount Type is required"),
            status: Yup.string().required("Status is required"),
            // discount_value: Yup.number().typeError("Discount Value must be a number").required("Discount Value is required"),
            // offer_type: Yup.string().required("Offer Type is required"),
            valid_from: Yup.string().required("Valid From is required"),
            valid_to: Yup.string().required("Valid To is required"),
        }),
        validateOnBlur: false,
        validateOnChange: false,
        validateOnMount: false,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const payload = {
                "offer_id": values.offer_id,
                "plan_id": values.plan_id,
                "offer_code": values.offer_code,
                "discount_type": values.discount_type,
                "status": values.status,
                // "discount_value": values.discount_value,
                // "offer_type": values.offer_type,
                "valid_from": values.valid_from,
                "valid_to": values.valid_to
            };

            if (selectedItem) {
                // console.log(payload, "selectedItem")
                updatePromoCodeAsync({ payload, _id: selectedItem?._id }, {
                    onSuccess: (response) => {
                        if (response?.data?.statusCode === 201 || response?.data?.statusCode === 200) {
                            onSuccess();
                            addToast("Update Offer Code Details", "success");
                            handleClose();
                        } else {
                            addToast(response?.data?.message, "error");
                        }
                    },
                    onError: ({ response }) => {
                        addToast(response?.data?.message, "error");
                    }
                });
            } else {
                createPromoCodeAsync(payload, {
                    onSuccess: (response) => {
                        if (response?.data?.statusCode === 201 || response?.data?.statusCode === 200) {
                            onSuccess();
                            addToast("Created Offer Code!", "success");
                            handleClose();
                        } else {
                            addToast(response?.data?.message, "error");
                        }
                    },
                    onError: ({ response }) => {
                        addToast(response?.data?.message, "error");
                    }
                });
            }
        },
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Remove spaces and trim the value
        const sanitizedValue = value.replace(/\s/g, '');
        formik.setFieldValue(name, sanitizedValue);
    };

    console.log(transformPlans(startupPlans)?.filter(
        plan => !formik.values?.plan_id?.includes(plan?.value)
    ))

    return (
        <ModalComponent open={open} onClose={handleClose} style={{ maxWidth: "600px", width: "100%" }}>
            <Paper
                sx={{
                    background: theme.palette.customColors.whiteBG,
                    borderRadius: "8px",
                }}
            >
                <IconButton sx={{ position: "absolute", top: 20, right: 20, padding: 0 }}>
                    <CloseIcon width={40} height={40} onClick={handleClose} />
                </IconButton>
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit} onKeyDown={(e) => {
                        if ((e.key === "Enter" && !e.shiftKey)) {
                            e.preventDefault();
                        }
                    }}>
                        <Grid item xs={12} padding={"1.5rem"}>
                            <Typography
                                sx={{
                                    fontSize: "1.5rem",
                                    fontWeight: 700,
                                    lineHeight: "28px",
                                    color: theme.palette.customColors.g300,
                                }}
                            >
                                {selectedItem ? "Update Offer Code Details" : "Create Offer Code"}
                            </Typography>
                        </Grid>
                        <Grid container padding={"0 1.5rem"} gap={"0rem"} flexWrap={"wrap"} spacing={2}>

                            <Grid item xs={12} sm={6} paddingTop={0}>
                                <CustomInputField
                                    name="offer_id"
                                    label="Offer ID"
                                    placeholder="Enter offer ID"
                                    value={formik?.values?.offer_id}
                                    onChange={handleInputChange}
                                    sx={{ marginBottom: 0 }}
                                    tabIndex={3}
                                    InputProps={{
                                        onKeyPress: (e) => {
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} paddingTop={0}>
                                <CustomInputField
                                    name="offer_code"
                                    label="Offer Code"
                                    placeholder="Enter offer code"
                                    value={formik?.values?.offer_code}
                                    onChange={(e) => {
                                        const inputValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                        formik.setFieldValue('offer_code', inputValue);
                                    }}
                                    sx={{ marginBottom: 0 }}
                                    tabIndex={3}
                                    InputProps={{
                                        onKeyPress: (e) => {
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }
                                    }
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <CustomLabel>Applicable On</CustomLabel>
                                <Autocomplete
                                    multiple
                                    disablePortal
                                    options={planOptions?.filter(
                                        plan => !formik.values?.plan_id?.includes(plan?.value)
                                    )}
                                    fullWidth
                                    value={
                                        formik.values?.plan_id?.length > 0
                                            ? formik.values?.plan_id.filter(Boolean).map(
                                                value => planOptions?.find(item => item?.value === value)
                                            ).filter(Boolean)
                                            : []
                                    }
                                    autoHighlight
                                    onChange={(_, newValue) => {
                                        const selectedSources = newValue.map(option => option.value);
                                        formik.setFieldValue("plan_id", selectedSources);
                                    }}
                                    renderTags={(selected, getTagProps) =>
                                        selected.map((option, index) => (
                                            <Chip
                                                key={option.value}
                                                label={<PlanOptionLabel option={option} />}
                                                {...getTagProps({ index })}
                                                sx={planChipSx(option)}
                                            />
                                        ))
                                    }
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.value}>
                                            <PlanOptionLabel option={option} />
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <CustomInputField
                                            variant="outlined"
                                            name={"plan_id"}
                                            placeholder={"Select plans"}
                                            {...params}
                                            sx={{
                                                marginBottom: 0
                                            }}
                                            className="filter-autocomplete"
                                        />
                                    )}
                                    sx={{
                                        "& .filter-autocomplete > .MuiInputBase-root": {
                                            borderRadius: "4px"
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <CustomInputField
                                    name="discount_type"
                                    label="Discount Type"
                                    select
                                    placeholder={"Select discount type"}
                                    value={formik?.values?.discount_type}
                                    onChange={formik.handleChange}
                                    sx={{
                                        marginBottom: 0,
                                        "&.Mui-selected": {
                                            backgroundColor: "#eae8fb !important",
                                        },
                                    }}
                                    tabIndex={6}
                                >
                                    {discountTypes.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </CustomInputField>
                            </Grid>

                            {/* Status Input */}
                            <Grid item xs={12} sm={6}>
                                <CustomInputField
                                    name="status"
                                    label="Status"
                                    select
                                    value={formik?.values?.status || "active"}
                                    onChange={formik.handleChange}
                                    sx={{ marginBottom: 0 }}
                                    tabIndex={7}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </CustomInputField>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            <Grid item xs={12} sm={6} paddingBottom={0}>
                                <CustomLabel>VALID FROM</CustomLabel>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label=""
                                        sx={{ width: "100%", background: "#fff", marginBottom: 0 }}
                                        value={formik.values?.valid_from ? moment(formik.values.valid_from) : null}
                                        disableFuture={false}
                                        onChange={(date) => {
                                            formik.setFieldValue('valid_from', date != null ? date.toISOString() : null);
                                        }}
                                        renderInput={(params) => (
                                            <CustomInputField
                                                sx={{ padding: "10px 14px" }}
                                                fullWidth
                                                {...params}
                                            />
                                        )}
                                        format="DD/MM/YYYY"
                                        autoComplete="off"
                                    />
                                </LocalizationProvider>
                                {formik.errors?.valid_from && <CustomValidationText value={formik.errors.valid_from} />}
                            </Grid>

                            <Grid item xs={12} sm={6} paddingBottom={0}>
                                <CustomLabel>VALID TO</CustomLabel>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label=""
                                        sx={{ width: "100%", background: "#fff", marginBottom: 0 }}
                                        value={formik.values?.valid_to ? moment(formik.values.valid_to) : null}
                                        disableFuture={false}
                                        onChange={(date) => {
                                            formik.setFieldValue('valid_to', date != null ? date.toISOString() : null);
                                        }}
                                        renderInput={(params) => (
                                            <CustomInputField
                                                sx={{ padding: "10px 14px" }}
                                                fullWidth
                                                {...params}
                                            />
                                        )}
                                        format="DD/MM/YYYY"
                                        autoComplete="off"
                                    />
                                </LocalizationProvider>
                                {formik.errors?.valid_to && <CustomValidationText value={formik.errors.valid_to} />}
                            </Grid>

                        </Grid>
                        <Grid item xs={12} padding={"1.5rem"}>
                            <Primary
                                type="submit"
                                fullWidth
                                sx={{ padding: "0.75rem 1.25rem", fontSize: "1rem", height: '52px' }}
                                disabled={isCreatingPromoCode || isUpdatingPromoCode}
                            >
                                {isCreatingPromoCode || isUpdatingPromoCode ? <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.2rem'} /> : selectedItem ? "Update" : "Create"}
                            </Primary>
                        </Grid>
                    </form>
                </FormikProvider>
            </Paper>
        </ModalComponent>
    );
};

export default UpdatePromoCode;